import React, { useEffect, useState } from "react";
import { adminApi } from "../../api/admin";
import { formatApiError } from "../../lib/errors";

const EMPTY_FORM = {
  name: "",
  email: "",
  code: "",
  country: "US",
  payout_method: "",
  payout_details: "",
  commission_rate: "30", // percent in the UI; sent to the API as a fraction
};

// Money must be formatted in its own currency — EUR and USD are never interchangeable.
const money = (cents, currency) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: (currency || "usd").toUpperCase(),
  }).format((cents || 0) / 100);

export default function AffiliatesPage() {
  const [affiliates, setAffiliates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");

  const fetchAffiliates = () => {
    adminApi
      .listAffiliates()
      .then((resp) => setAffiliates(resp.data.affiliates))
      .catch((err) => setError(formatApiError(err, "Failed to load affiliates")));
  };
  useEffect(() => { fetchAffiliates(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault(); setError("");
    try {
      const pct = parseFloat(form.commission_rate);
      await adminApi.createAffiliate({
        ...form,
        // UI collects a percent (30); the API stores a fraction (0.30).
        commission_rate: Number.isFinite(pct) ? pct / 100 : 0.3,
      });
      setShowForm(false); setForm(EMPTY_FORM); fetchAffiliates();
    } catch (err) {
      if (err?.response?.status === 409) setError("Code already in use");
      else setError(formatApiError(err, "Failed to create affiliate"));
    }
  };

  const handleMarkPaid = async (id, currency, pendingCents) => {
    if (!window.confirm(
      `Mark ${money(pendingCents, currency)} as paid to this affiliate? This can't be undone.`
    )) return;
    await adminApi.markAffiliatePaid(id, currency); fetchAffiliates();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-clash text-2xl font-bold text-white">Affiliates</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary px-6 py-2 rounded text-sm font-semibold">{showForm ? "Cancel" : "New Affiliate"}</button>
      </div>
      {showForm && (
        <div className="feature-card rounded-lg p-6 mb-6">
          {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded mb-4 text-sm">{error}</div>}
          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">Name</label>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-[#1a1a1e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#FF5500] focus:outline-none" required placeholder="Jane Producer" />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-[#1a1a1e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#FF5500] focus:outline-none" required placeholder="jane@example.com" />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">Code</label>
              <input type="text" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                className="w-full bg-[#1a1a1e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#FF5500] focus:outline-none" required placeholder="JANE20" />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">Country</label>
              <input type="text" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="w-full bg-[#1a1a1e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#FF5500] focus:outline-none" placeholder="US" />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">Commission %</label>
              <input type="number" value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: e.target.value })}
                className="w-full bg-[#1a1a1e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#FF5500] focus:outline-none" min="1" max="90" step="1" required placeholder="30" />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">Payout Method</label>
              <input type="text" value={form.payout_method} onChange={(e) => setForm({ ...form, payout_method: e.target.value })}
                className="w-full bg-[#1a1a1e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#FF5500] focus:outline-none" placeholder="PayPal" />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">Payout Details</label>
              <input type="text" value={form.payout_details} onChange={(e) => setForm({ ...form, payout_details: e.target.value })}
                className="w-full bg-[#1a1a1e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#FF5500] focus:outline-none" placeholder="jane@paypal.com" />
            </div>
            <div className="md:col-span-2">
              <button type="submit" className="btn-primary px-8 py-2 rounded text-sm font-semibold">Create Affiliate</button>
            </div>
          </form>
        </div>
      )}
      <div className="space-y-2">
        {affiliates.map((a) => {
          const currencies = Object.keys(a.stats?.by_currency || {}).sort();
          return (
          <div key={a._id} className="feature-card rounded-lg px-6 py-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-white text-sm font-semibold min-w-0">
                {a.name}
                <span className="ml-2 font-mono text-[#FF5500]">{a.code}</span>
                <span className="ml-2 text-[#A1A1AA]">{a.country}</span>
                <span className="ml-2 text-[#A1A1AA]">&middot; {Math.round((a.commission_rate ?? 0.3) * 100)}%</span>
              </p>
              <span className="text-xs text-[#A1A1AA]">{a.stats?.sales_count || 0} sales</span>
            </div>
            <div className="mt-3 space-y-1">
              {currencies.length === 0 && (
                <p className="text-xs text-[#A1A1AA]/60">No sales yet</p>
              )}
              {currencies.map((cur) => {
                const s = a.stats.by_currency[cur];
                return (
                  <div key={cur} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs">
                    <p className="text-[#A1A1AA]">
                      <span className="font-mono uppercase text-white">{cur}</span> &middot;
                      Earned {money(s.total_commission_cents, cur)} &middot;
                      Owed {money(s.pending_cents, cur)} &middot;
                      Paid {money(s.paid_cents, cur)}
                    </p>
                    <button onClick={() => handleMarkPaid(a._id, cur, s.pending_cents)} disabled={!s.pending_cents}
                      className="md:ml-4 shrink-0 text-[#FF5500] hover:text-[#FF7733] disabled:text-[#A1A1AA]/40 disabled:cursor-not-allowed">Mark paid</button>
                  </div>
                );
              })}
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}
